import React from "react";
import { Box, Chip } from "@mui/material";
import { formatDateTime } from "utils/formatters";
import DataTable from "../../../components/common/tables/DataTable";
import SectionCard from "../components/SectionCard";
import StatusChip from "../components/StatusChip";
import {
  getHistoryActionLabel,
  getHistoryActionStyle,
} from "../utils/subcontracts/historyRules";

const actionChipCell = ({ cell, row }) => {
  const value = cell.getValue();
  if (!value) return "-";
  const label = getHistoryActionLabel(value, row?.original?.entityType);
  const style = getHistoryActionStyle(value);

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        height: 24,
        fontWeight: 600,
        fontSize: 11,
        color: style.color,
        backgroundColor: style.background,
        borderColor: style.border,
      }}
    />
  );
};

const actorCell = ({ row }) => row.original.changedByName || (row.original.changedBy ? `Người dùng #${row.original.changedBy}` : "-");
const timeCell = ({ cell }) => (cell.getValue() ? formatDateTime(cell.getValue()) : "-");
const statusCell = ({ cell }) => (cell.getValue() ? <StatusChip value={cell.getValue()} /> : "-");
const textCell = ({ cell }) => cell.getValue() || "-";

export default function HistoryTab({
  history = { contract: [], status: [], items: [], approvals: [], acceptanceApprovals: [] },
}) {
  const contractData = Array.isArray(history?.contract)
    ? history.contract
    : (Array.isArray(history?.contract?.items) ? history.contract.items : []);

  const statusData = Array.isArray(history?.status)
    ? history.status
    : (Array.isArray(history?.status?.data) ? history.status.data : []);

  const itemsData = Array.isArray(history?.items)
    ? history.items
    : (Array.isArray(history?.items?.items) ? history.items.items : []);

  const approvalsData = Array.isArray(history?.approvals)
    ? history.approvals
    : (Array.isArray(history?.approvals?.data) ? history.approvals.data : []);

  const acceptanceApprovalsData = Array.isArray(history?.acceptanceApprovals)
    ? history.acceptanceApprovals
    : (Array.isArray(history?.acceptanceApprovals?.data) ? history.acceptanceApprovals.data : []);

  const subcontractColumns = [
    { accessorKey: "changeType", header: "Hành động", Cell: actionChipCell, size: 120 },
    {
      accessorKey: "fieldDisplayName",
      header: "Nội dung",
      Cell: ({ row }) => row.original.fieldDisplayName || row.original.fieldName || "-",
    },
    { accessorKey: "oldValue", header: "Giá trị cũ", Cell: textCell },
    { accessorKey: "newValue", header: "Giá trị mới", Cell: textCell },
    { accessorKey: "changedBy", header: "Người thực hiện", Cell: textCell },
    { accessorKey: "changedAt", header: "Thời gian", Cell: timeCell, size: 100 },
    { accessorKey: "changeReason", header: "Lý do", Cell: textCell },
  ];

  const statusColumns = [
    { accessorKey: "oldStatus", header: "Trạng thái cũ", Cell: statusCell },
    { accessorKey: "newStatus", header: "Trạng thái mới", Cell: statusCell },
    { accessorKey: "changedBy", header: "Người thực hiện", Cell: textCell },
    { accessorKey: "changedAt", header: "Thời gian", Cell: timeCell, size: 100 },
    { accessorKey: "changeReason", header: "Lý do", Cell: textCell },
  ];

  const itemColumns = [
    { accessorKey: "itemCode", header: "STT", Cell: textCell, size: 50 },
    { accessorKey: "itemName", header: "Dự toán", Cell: textCell, size: 200 },
    { accessorKey: "changeType", header: "Hành động", Cell: actionChipCell, size: 80 },
    {
      accessorKey: "fieldDisplayName",
      header: "Nội dung",
      Cell: ({ row }) => row.original.fieldDisplayName || row.original.fieldName || "-",
    },
    { accessorKey: "oldValue", header: "Giá trị cũ", Cell: textCell, size: 200 },
    { accessorKey: "newValue", header: "Giá trị mới", Cell: textCell, size: 200 },
    { accessorKey: "changedBy", header: "Người thực hiện", Cell: textCell },
    { accessorKey: "changedAt", header: "Thời gian", Cell: timeCell, size: 100 },
    { accessorKey: "changeReason", header: "Lý do", Cell: textCell },
  ];

  const approvalColumns = [
    { accessorKey: "approvalProfileId", header: "Hồ sơ", enableHiding: false },
    { accessorKey: "action", header: "Hành động", Cell: actionChipCell, size: 80 },
    { accessorKey: "oldStatus", header: "Trạng thái cũ", Cell: statusCell },
    { accessorKey: "newStatus", header: "Trạng thái mới", Cell: statusCell },
    { accessorKey: "changedByName", header: "Người thực hiện", Cell: actorCell },
    { accessorKey: "changedAt", header: "Thời gian", Cell: timeCell, size: 100 },
    { accessorKey: "comment", header: "Ý kiến", Cell: textCell },
  ];

  const acceptanceApprovalColumns = [
    { accessorKey: "acceptanceApprovalProfileId", header: "Hồ sơ", enableHiding: false },
    { accessorKey: "action", header: "Hành động", Cell: actionChipCell, size: 80 },
    { accessorKey: "oldStatus", header: "Trạng thái cũ", Cell: statusCell },
    { accessorKey: "newStatus", header: "Trạng thái mới", Cell: statusCell },
    { accessorKey: "changedByName", header: "Người thực hiện", Cell: actorCell },
    { accessorKey: "changedAt", header: "Thời gian", Cell: timeCell, size: 100 },
    { accessorKey: "comment", header: "Ý kiến", Cell: textCell },
  ];

  return (
    <Box className="subcontract-stack">
      <SectionCard title="Lịch sử phê duyệt quyết định giao khoán">
        <DataTable
          columns={approvalColumns}
          data={approvalsData}
          initialState={{
            columnVisibility: {
              approvalProfileId: false,
            },
          }}
        />
      </SectionCard>
      <SectionCard title="Lịch sử phê duyệt nghiệm thu">
        <DataTable
          columns={acceptanceApprovalColumns}
          data={acceptanceApprovalsData}
          initialState={{
            columnVisibility: {
              acceptanceApprovalProfileId: false,
            },
          }}
        />
      </SectionCard>
      <SectionCard title="Lịch sử quyết định giao khoán">
        <DataTable columns={subcontractColumns} data={contractData} />
      </SectionCard>
      <SectionCard title="Lịch sử trạng thái quyết định giao khoán">
        <DataTable columns={statusColumns} data={statusData} />
      </SectionCard>
      <SectionCard title="Lịch sử dự toán quyết định giao khoán">
        <DataTable columns={itemColumns} data={itemsData} />
      </SectionCard>
    </Box>
  );
}
